/*
 * Copyright 2021 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Classes, ContextMenu2ContentProps, ContextMenu2Props, IPopover2TargetProps, Popover2} from '@api'
import classNames from "classnames";
import {React} from '@api'

import {
  Classes as CoreClasses,
  Utils as CoreUtils,
  mergeRefs,
} from "@blueprintjs/core";


type Offset = {
  left: number;
  top: number;
}

export const MyContextMenu2: React.FC<ContextMenu2Props & {portalClassName?: string, popoverClassName?: string}> = function MyContextMenu2({
  className,
  children,
  content,
  disabled = false,
  onContextMenu,
  popoverClassName,
  portalClassName,
  ...restProps
}) {
  const [targetOffset, setTargetOffset] = React.useState<Offset>({left: 0, top: 0});
  const [mouseEvent, setMouseEvent] = React.useState<React.MouseEvent<HTMLElement>>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // If disabled prop is changed, we don't want our old context menu to stick around.
  // If it has just been enabled (disabled = false), then the menu ought to be opened by
  // a new mouse event. Users should not be updating this prop in the onContextMenu callback
  // for this component (that will lead to unpredictable behavior).
  React.useEffect(() => {
    setIsOpen(false);
  }, [disabled]);

  const cancelContextMenu = React.useCallback((e: React.SyntheticEvent<HTMLDivElement>) => e.preventDefault(), []);

  const handlePopoverInteraction = React.useCallback((nextOpenState: boolean) => {
    if (!nextOpenState) {
      setIsOpen(false);
      setMouseEvent(undefined);
    }
  }, []);

  const targetRef = React.useRef<HTMLDivElement>(null);
  const renderTarget = React.useCallback(
    ({ref}: IPopover2TargetProps) => (
      <div
        className={Classes.CONTEXT_MENU2_VIRTUAL_TARGET}
        style={targetOffset}
        ref={mergeRefs(ref, targetRef)}
      />
    ),
    [targetOffset],
  );
  const isDarkTheme = React.useMemo(() => CoreUtils.isDarkTheme(targetRef.current), [targetRef.current]);

  const contentProps: ContextMenu2ContentProps = {isOpen, mouseEvent, targetOffset};

  // only render the popover if there is content in the context menu;
  // this avoid doing unnecessary rendering & computation
  const menu = disabled ? undefined : CoreUtils.isFunction(content) ? content(contentProps) : content;
  const maybePopover =
    menu === undefined ? undefined : (
      <Popover2
        {...restProps}
        content={
          // this prevents right-clicking inside our context menu
          <div /*onContextMenu={cancelContextMenu}*/>{menu}</div>
        }
        enforceFocus={false}
        // Generate key based on offset so a new Popover instance is created
        // when offset changes, to force recomputing position.
        key={`${targetOffset.left}x${targetOffset.top}`}
        hasBackdrop={true}
        isOpen={isOpen}
        minimal={true}
        onInteraction={handlePopoverInteraction}
        popoverClassName={classNames(popoverClassName, {[CoreClasses.DARK]: isDarkTheme})}
        portalClassName={classNames(portalClassName)}
        placement="right-start"
        positioningStrategy="fixed"
        rootBoundary="viewport"
        renderTarget={renderTarget}
      />
    );

  const handleContextMenu = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // support nested menus (inner menu target would have called preventDefault())
      if (e.defaultPrevented) {
        return;
      }

      if (!disabled) {
        e.preventDefault();
        e.persist();
        setMouseEvent(e);
        const {left, top} = getContainingBlockOffset(containerRef.current);
        setTargetOffset({left: e.clientX - left, top: e.clientY - top});
        setIsOpen(true);
      }

      onContextMenu?.(e);
    },
    [containerRef.current, onContextMenu, disabled],
  );

  const containerClassName = classNames(className, Classes.CONTEXT_MENU2);

  if (CoreUtils.isFunction(children)) {
    return children({
      className: containerClassName,
      contentProps,
      onContextMenu: handleContextMenu,
      popover: maybePopover,
      ref: containerRef,
    });
  } else {
    return (
      <div className={containerClassName} ref={containerRef} onContextMenu={handleContextMenu}>
        {maybePopover}
        {children}
      </div>
    );
  }
};

function getContainingBlockOffset(targetElement: HTMLElement | null | undefined): { left: number; top: number } {
  if (targetElement != null) {
    const containingBlock = targetElement.closest(`.${CoreClasses.FIXED_POSITIONING_CONTAINING_BLOCK}`);
    if (containingBlock != null) {
      return containingBlock.getBoundingClientRect();
    }
  }
  return {left: 0, top: 0};
}



// WEBPACK FOOTER //
// ./src/modules/contextMenus/BP3ContextMenu.tsx